<i18n locale="en">
{
  "aria": {
    "retry": "Retry image generation",
    "ready": "Generated image",
    "placeholder": "Generate new image",
    "pending": "Generating image, please wait",
    "failed": "Image generation failed, click to retry",
    "default": "Gallery item"
  }
}
</i18n>

<template>
  <div
    role="gridcell"
    class="image-gallery-item"
    :class="[
      `image-gallery-item--${item.state}`,
      { 'image-gallery-item--focused': isFocused },
    ]"
    :tabindex="isFocused ? 0 : -1"
    :aria-label="ariaLabel"
    @click="handleClick"
  >
    <template v-if="item.state === 'ready' && item.media">
      <img
        :src="item.media.url || ''"
        :alt="item.media.id"
        class="image-gallery-item__image"
        loading="lazy"
      />
    </template>
    <template v-else-if="item.state === 'placeholder'">
      <PhPlus :size="32" aria-hidden="true" />
    </template>
    <template v-else-if="item.state === 'pending' || item.state === 'failed'">
      <img
        v-if="item.parentImageUrl"
        :src="item.parentImageUrl"
        :alt="item.id"
        class="image-gallery-item__image"
        loading="lazy"
      />
      <div v-if="item.state === 'failed'" class="image-gallery-item__overlay">
        <button
          class="image-gallery-item__retry-button"
          :aria-label="t('aria.retry')"
          @click.stop="$emit('retry', item)"
        >
          <PhArrowClockwise :size="20" aria-hidden="true" />
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { PhArrowClockwise, PhPlus } from '@phosphor-icons/vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { GalleryItem } from './types'

interface Props {
  item: GalleryItem
  isFocused?: boolean
}

interface Emits {
  (event: 'click', item: GalleryItem): void
  (event: 'retry', item: GalleryItem): void
}

const props = withDefaults(defineProps<Props>(), {
  isFocused: false,
})
const emit = defineEmits<Emits>()

const { t } = useI18n()

const ariaLabel = computed(() => {
  switch (props.item.state) {
    case 'ready':
      return t('aria.ready')
    case 'placeholder':
      return t('aria.placeholder')
    case 'pending':
      return t('aria.pending')
    case 'failed':
      return t('aria.failed')
    default:
      return t('aria.default')
  }
})

/**
 *
 */
function handleClick(): void {
  if (props.item.state !== 'failed') {
    emit('click', props.item)
  }
}
</script>

<style lang="scss" scoped>
.image-gallery-item {
  position: relative;
  width: 100%;
  padding-bottom: 100%; // 1:1 aspect ratio hack
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  outline: none;
  transition: box-shadow 0.2s ease;

  &--focused {
    box-shadow: 0 0 0 3px var(--color-primary, #007aff);
  }

  &--ready {
    .image-gallery-item__image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &--placeholder {
    border: 2px dashed var(--border-secondary);
    background: var(--background-secondary);

    // Center the + icon absolutely
    > :first-child {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &--pending {
    .image-gallery-item__image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: blur(20px);
      animation: pulse 2s ease-in-out infinite;
    }
  }

  &--failed {
    .image-gallery-item__image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: blur(20px);
    }
  }
}

.image-gallery-item__overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(239, 68, 68, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-gallery-item__retry-button {
  background: white;
  border: 1px solid var(--border-secondary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
